/**
 * OutcomeBadge.tsx
 * P4A Phase 6: Displays cohort-level outcome data on ActionCards.
 *
 * Privacy: Only aggregate metrics. No member PII.
 * Non-causal: All wording is observational — never implies causation.
 *
 * Display states:
 *   pending       → "Measuring outcomes…" (spinner)
 *   measured +    → top improvements (green)
 *   measured −    → worsened metrics (amber)
 *   unchanged     → "No significant change observed" (muted)
 *   insufficient  → hidden (filtered by API)
 */

import { FC } from 'react';
import { FiTrendingUp, FiTrendingDown, FiClock, FiMinusCircle } from 'react-icons/fi';
import type { RecommendationOutcome, MetricDelta } from '../../services/recommendationOutcomesService';

interface OutcomeBadgeProps {
  outcome: RecommendationOutcome;
}

/** Human-readable metric labels (non-causal) */
const METRIC_LABELS: Record<string, string> = {
  score: 'VeraScore',
  dti: 'DTI ratio',
  reserves_months: 'Reserves',
  cashflow: 'Cashflow',
};

/** Format a delta value for display */
function formatDelta(key: string, delta: number): string {
  const sign = delta > 0 ? '+' : '';
  if (key === 'dti') return `${sign}${(delta * 100).toFixed(1)}%`;
  if (key === 'reserves_months') return `${sign}${delta.toFixed(1)} mo`;
  return `${sign}${delta.toFixed(1)} pts`;
}

/** Confidence indicator */
function ConfidenceChip({ level }: { level: string }) {
  const config = {
    HIGH: { color: 'bg-green-100 text-green-700 border-green-200', dot: '🟢' },
    MEDIUM: { color: 'bg-amber-100 text-amber-700 border-amber-200', dot: '🟡' },
    LOW: { color: 'bg-orange-100 text-orange-700 border-orange-200', dot: '🟠' },
  }[level] ?? { color: 'bg-slate-100 text-slate-500 border-slate-200', dot: '⚪' };

  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${config.color}`}>
      <span className="mr-0.5">{config.dot}</span>
      {level}
    </span>
  );
}

/** Get top N deltas sorted by absolute magnitude, excluding noise-suppressed / null */
function getTopDeltas(
  deltas: NonNullable<RecommendationOutcome['deltas']>,
  noiseSuppressed: boolean,
  maxCount = 3,
): Array<{ key: string; delta: number; label: string; direction: 'up' | 'down' }> {
  if (noiseSuppressed) return [];

  const entries: Array<{ key: string; delta: number; label: string; direction: 'up' | 'down' }> = [];
  for (const [key, metric] of Object.entries(deltas) as [string, MetricDelta][]) {
    if (metric.delta !== null && metric.delta !== 0) {
      // For DTI, lower is better → invert direction
      const isImprovement = key === 'dti' ? metric.delta < 0 : metric.delta > 0;
      entries.push({
        key,
        delta: metric.delta,
        label: METRIC_LABELS[key] || key,
        direction: isImprovement ? 'up' : 'down',
      });
    }
  }

  return entries
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, maxCount);
}

const OutcomeBadge: FC<OutcomeBadgeProps> = ({ outcome }) => {
  // ── Pending state ──
  if (outcome.status === 'pending') {
    return (
      <div className="mt-3 px-3 py-2 bg-slate-50 rounded-md border border-slate-200">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <FiClock size={12} className="animate-pulse" />
          <span>Measuring outcomes… ({outcome.outcome_window_days}-day window)</span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <span className="text-[10px] text-slate-400">
            Cohort: {outcome.cohort_size} members
          </span>
          <ConfidenceChip level={outcome.confidence.level} />
        </div>
      </div>
    );
  }

  // ── Measured state ──
  const { deltas, noise_suppressed } = outcome;

  // Unchanged / noise-suppressed
  if (!deltas || noise_suppressed) {
    return (
      <div className="mt-3 px-3 py-2 bg-slate-50 rounded-md border border-slate-200">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <FiMinusCircle size={12} />
          <span>No significant change observed</span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <span className="text-[10px] text-slate-400">
            Cohort: {outcome.cohort_size} members
          </span>
          <ConfidenceChip level={outcome.confidence.level} />
        </div>
      </div>
    );
  }

  const topDeltas = getTopDeltas(deltas, noise_suppressed);

  // All deltas are exactly zero (not noise-suppressed but still zero)
  if (topDeltas.length === 0) {
    return (
      <div className="mt-3 px-3 py-2 bg-slate-50 rounded-md border border-slate-200">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <FiMinusCircle size={12} />
          <span>No significant change observed</span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <span className="text-[10px] text-slate-400">
            Cohort: {outcome.cohort_size} members
          </span>
          <ConfidenceChip level={outcome.confidence.level} />
        </div>
      </div>
    );
  }

  const hasImprovement = topDeltas.some((d) => d.direction === 'up');
  const borderColor = hasImprovement ? 'border-green-200' : 'border-amber-200';
  const bgColor = hasImprovement ? 'bg-green-50' : 'bg-amber-50';

  return (
    <div className={`mt-3 px-3 py-2 ${bgColor} rounded-md border ${borderColor}`}>
      {/* Non-causal header */}
      <div className="text-[10px] text-slate-500 mb-1 italic">
        Observed after this recommendation
      </div>
      {/* Delta lines */}
      <div className="space-y-0.5">
        {topDeltas.map(({ key, delta, label, direction }) => (
          <div key={key} className="flex items-center gap-1.5 text-xs">
            {direction === 'up' ? (
              <FiTrendingUp size={12} className="text-green-600 flex-shrink-0" />
            ) : (
              <FiTrendingDown size={12} className="text-amber-600 flex-shrink-0" />
            )}
            <span className={direction === 'up' ? 'text-green-700 font-medium' : 'text-amber-700 font-medium'}>
              {formatDelta(key, delta)}
            </span>
            <span className="text-slate-500">{label}</span>
          </div>
        ))}
      </div>
      {/* Footer: cohort + confidence */}
      <div className="mt-1.5 flex items-center gap-2">
        <span className="text-[10px] text-slate-400">
          Cohort: {outcome.cohort_size} members
        </span>
        <ConfidenceChip level={outcome.confidence.level} />
      </div>
    </div>
  );
};

export default OutcomeBadge;
